/**
 * Insight wire ⇄ domain mappers.
 *
 * The intelligence-service returns snake_case with a numeric `insight_id` /
 * `customer_id`; the UI uses string ids. We map ids to strings on the way in
 * and accept either a bare array or the `{ items: [...] }` list envelope.
 *
 * NOTE: confirm field names against the live intelligence-service contract
 * during real-mode verification and adjust the overrides here only.
 */

import type {
  Insight,
  InsightStatus,
  InsightType,
  GenerateInsightsResult,
  GenerateInsightsInput,
} from '@/types/insight.types'

function str(value: unknown): string {
  return value === null || value === undefined ? '' : String(value)
}

export function mapInsightFromApi(raw: Record<string, unknown>): Insight {
  const recommendations = Array.isArray(raw.recommendations)
    ? (raw.recommendations as unknown[]).map(str)
    : []
  const confidence = Number(raw.confidence_score)
  return {
    id: str(raw.insight_id ?? raw.id),
    customerId: str(raw.customer_id),
    insightType: str(raw.insight_type) as InsightType,
    title: str(raw.title),
    description: str(raw.description),
    recommendations,
    confidenceScore: Number.isFinite(confidence) ? confidence : 0,
    status: str(raw.status) as InsightStatus,
    createdAt: str(raw.created_at),
    updatedAt: str(raw.updated_at),
  }
}

export function mapInsightListFromApi(raw: unknown): Insight[] {
  const arr = Array.isArray(raw)
    ? raw
    : ((raw as { items?: unknown[] })?.items ?? [])
  return (arr as Record<string, unknown>[]).map(mapInsightFromApi)
}

export function mapGenerateToApi(input: GenerateInsightsInput): Record<string, unknown> {
  const body: Record<string, unknown> = {
    customer_id: Number(input.customerId),
    force_refresh: input.forceRefresh ?? false,
  }
  if (input.insightTypes && input.insightTypes.length > 0) {
    body.insight_types = input.insightTypes
  }
  return body
}

export function mapGenerateResultFromApi(
  raw: Record<string, unknown>
): GenerateInsightsResult {
  const ids = Array.isArray(raw.insight_ids)
    ? (raw.insight_ids as unknown[]).map(str)
    : []
  return {
    customerId: str(raw.customer_id),
    insightsGenerated: Number(raw.insights_generated) || 0,
    insightIds: ids,
    message: str(raw.message),
  }
}
